import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import ReactMarkdown from "react-markdown";

const makeConversationId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `conv_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

export default function DriveChatbot() {
    const [ingestions, setIngestions] = useState([]);
    const [loadingIngestions, setLoadingIngestions] = useState(true);
    const [listError, setListError] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    const [question, setQuestion] = useState("");
    
    // Conversation State
    const [conversationId, setConversationId] = useState(() => {
        return localStorage.getItem("drive_rag_conversation_id") || makeConversationId();
    });
    const [messages, setMessages] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [asking, setAsking] = useState(false);
    
    const messagesEndRef = useRef(null);

    const fetchIngestions = useCallback(async () => {
        const response = await fetchWithAuth("/api/drive_rag/new/list-ingestions");
        if (!response) throw new Error("No response");
        if (response.status === 404) return [];
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.detail || "Unable to fetch ingestions");
        }
        const data = await response.json().catch(() => ({}));
        return Array.isArray(data?.ingestions) ? data.ingestions : [];
    }, []);

    const loadIngestions = useCallback(async () => {
        setLoadingIngestions(true);
        setListError("");
        try {
            const items = await fetchIngestions();
            setIngestions(items);
            if (items.length) {
                setSelectedIds(prev => {
                    const existing = prev.filter(id => items.some(item => (item.ingestion_id || item.document_id || "") === id));
                    if (existing.length) return existing;
                    const first = items[0].ingestion_id || items[0].document_id || "";
                    return first ? [first] : [];
                });
            } else {
                setSelectedIds([]);
            }
        } catch (error) {
            setListError(error.message || "Failed to load ingestions");
            setIngestions([]);
            setSelectedIds([]);
        } finally {
            setLoadingIngestions(false);
        }
    }, [fetchIngestions]);

    useEffect(() => {
        loadIngestions();
    }, [loadIngestions]);

    // Load conversation history when conversationId changes
    useEffect(() => {
        localStorage.setItem("drive_rag_conversation_id", conversationId);
        
        const loadHistory = async () => {
            setLoadingHistory(true);
            try {
                const response = await fetchWithAuth(`/api/drive_rag/new/chat-history?conversation_id=${conversationId}`);
                if (response?.ok) {
                    const data = await response.json();
                    setMessages(data.messages || []);
                } else {
                    setMessages([]);
                }
            } catch (error) {
                console.error("Failed to load history", error);
                setMessages([]);
            } finally {
                setLoadingHistory(false);
            }
        };

        loadHistory();
    }, [conversationId]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, asking]);

    const handleNewChat = () => {
        setConversationId(makeConversationId());
        setMessages([]);
        setQuestion("");
    };

    const handleAsk = async (event) => {
        event.preventDefault();
        if (!selectedIds.length) {
            alert("Please select at least one ingestion folder to query.");
            return;
        }
        if (!question.trim()) return;

        const userMessage = {
            id: Date.now().toString(),
            text: question.trim(),
            sender: "user"
        };

        setMessages(prev => [...prev, userMessage]);
        setAsking(true);
        setQuestion("");

        try {
            const response = await fetchWithAuth("/api/drive_rag/new/ask", {
                method: "POST",
                body: JSON.stringify({
                    query: userMessage.text,
                    ingestion_id: selectedIds,
                    conversation_id: conversationId
                })
            });
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || data.message || "Request failed");
            }

            const botMessage = {
                id: Date.now().toString() + "-bot",
                text: data.answer,
                sender: "bot",
                snippets: data.snippets || []
            };
            setMessages(prev => [...prev, botMessage]);
            
            // In case backend changes the conversation ID for some reason
            if (data.conversation_id && data.conversation_id !== conversationId) {
                setConversationId(data.conversation_id);
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                id: Date.now().toString() + "-err",
                text: "Error: " + error.message,
                sender: "bot",
                isError: true
            }]);
        } finally {
            setAsking(false);
        }
    };

    return (
        <div className="flex h-full flex-col gap-6 overflow-y-auto bg-slate-50 px-4 py-6 md:px-10 md:py-8 text-slate-700">
            <header className="space-y-1">
                <h1 className="text-3xl font-semibold text-slate-900">Drive Assistant</h1>
                <p className="text-sm text-slate-500">
                    Chat interactively with your files and folders explicitly selected below.
                </p>
            </header>

            <section className="grid flex-1 gap-6 lg:grid-cols-[280px_1fr] min-h-0">
                {/* Left Sidebar - Folder Selections */}
                <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm overflow-y-auto h-fit max-h-full">
                    <div className="flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Data Sources</h2>
                        <button
                            type="button"
                            onClick={loadIngestions}
                            className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-medium text-slate-500 transition hover:border-purple-200 hover:text-purple-600"
                        >
                            REFRESH
                        </button>
                    </div>
                    
                    <div className="mt-4 flex-1">
                        {loadingIngestions ? (
                            <div className="flex items-center space-x-2 text-sm text-slate-400">
                                <svg className="animate-spin h-4 w-4 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                <span>Loading files...</span>
                            </div>
                        ) : listError ? (
                            <p className="text-sm text-red-500">{listError}</p>
                        ) : ingestions.length === 0 ? (
                            <p className="text-sm text-slate-500">No ingested folders found.</p>
                        ) : (
                            <div className="space-y-2">
                                <p className="text-[11px] text-slate-400">Select standard folders to ground the AI:</p>
                                <select
                                    multiple
                                    size={Math.min(Math.max(ingestions.length || 3, 3), 15)}
                                    value={selectedIds}
                                    onChange={event => {
                                        const values = Array.from(event.target.selectedOptions).map(option => option.value);
                                        setSelectedIds(values);
                                    }}
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200 scrollbar-thin"
                                >
                                    {ingestions.map(ing => {
                                        const ingestionId = ing.ingestion_id || ing.document_id || "";
                                        return (
                                            <option key={ingestionId} value={ingestionId} className="py-1">
                                                📁 {ing.folder_name || ingestionId}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Chat UI */}
                <div className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden min-h-[500px]">
                    {/* Chat Header */}
                    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-3 backdrop-blur-md">
                        <div className="flex items-center space-x-2">
                            <div className="h-2 w-2 rounded-full bg-green-500"></div>
                            <h2 className="text-sm font-semibold text-slate-700">Chat Session</h2>
                        </div>
                        <button 
                            onClick={handleNewChat} 
                            className="rounded-full bg-slate-200/50 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-purple-100 hover:text-purple-700"
                        >
                            + New Chat
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-5 scrollbar-thin scrollbar-thumb-slate-200 bg-white">
                        {loadingHistory && (
                            <div className="flex justify-center py-4">
                                <span className="text-xs text-slate-400 font-medium tracking-wide">LOADING HISTORY...</span>
                            </div>
                        )}
                        
                        {messages.length === 0 && !loadingHistory && (
                            <div className="flex h-full flex-col items-center justify-center text-center opacity-70">
                                <div className="mb-4 rounded-full bg-purple-50 p-4">
                                    <svg className="h-8 w-8 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-slate-800">How can I help you?</h3>
                                <p className="mt-2 max-w-sm text-sm text-slate-500">
                                    Ask any question based on the folders you selected in the left panel.
                                </p>
                            </div>
                        )}

                        {messages.map((msg, i) => (
                            <ChatMessage key={msg.id || i} message={msg} />
                        ))}

                        {asking && (
                            <div className="flex justify-start animate-pulse">
                                <div className="rounded-2xl rounded-tl-sm border border-slate-100 bg-slate-50 px-5 py-3 text-sm text-slate-500 flex items-center space-x-2">
                                    <div className="flex space-x-1">
                                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce"></div>
                                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                                    </div>
                                    <span>Analyzing documents...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} className="h-1" />
                    </div>

                    {/* Chat Input */}
                    <div className="border-t border-slate-100 bg-white p-4">
                        <form onSubmit={handleAsk} className="flex items-end gap-3 rounded-2xl border border-slate-300 bg-white px-2 py-2 focus-within:border-purple-500 focus-within:ring-1 focus-within:ring-purple-500 shadow-sm transition-shadow">
                            <textarea
                                className="max-h-32 min-h-[44px] w-full resize-none border-none bg-transparent px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-0"
                                placeholder="Type your message here..."
                                value={question}
                                onChange={e => setQuestion(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAsk(e);
                                    }
                                }}
                                disabled={asking}
                                rows={1}
                            />
                            <button
                                type="submit"
                                disabled={asking || !question.trim()}
                                className="mb-1 flex shrink-0 h-9 w-9 items-center justify-center rounded-full bg-purple-600 text-white transition hover:bg-purple-700 disabled:opacity-40"
                            >
                                <svg className="h-4 w-4 translate-x-px -translate-y-px" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </form>
                        <div className="mt-2 text-center text-[10px] text-slate-400">
                            Press Enter to send, Shift + Enter for new line.
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ChatMessage({ message }) {
    const isUser = message.sender === "user";

    if (message.isError) {
        return (
            <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-600 shadow-sm">
                    {message.text}
                </div>
            </div>
        );
    }

    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-5 py-4 text-sm shadow-sm ${
                isUser 
                ? "rounded-tr-sm bg-purple-600 text-white" 
                : "rounded-tl-sm border border-slate-200 bg-white text-slate-800"
            }`}>
                {isUser ? (
                    <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
                ) : (
                    <div className="prose prose-sm max-w-none text-slate-800 marker:text-purple-400 prose-a:text-purple-600 hover:prose-a:text-purple-800">
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                    </div>
                )}

                {/* Grounding / Source Snippets rendered only for bot messages */}
                {!isUser && Array.isArray(message.snippets) && message.snippets.length > 0 && (
                    <div className="mt-4 border-t border-slate-100 pt-3">
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Sources / Citations</p>
                        <div className="flex flex-wrap gap-2">
                            {message.snippets.slice(0, 5).map((snippet, idx) => (
                                <div key={idx} className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-600 transition hover:bg-slate-100 hover:border-slate-300">
                                    <svg className="mr-1.5 h-3.5 w-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                    {snippet.url ? (
                                        <a href={snippet.url} target="_blank" rel="noreferrer" className="font-medium text-slate-700 hover:text-purple-700 hover:underline decoration-purple-300 decoration-2 underline-offset-2">
                                            {snippet.name}
                                        </a>
                                    ) : (
                                        <span className="font-medium text-slate-700">{snippet.name}</span>
                                    )}
                                </div>
                            ))}
                            {message.snippets.length > 5 && (
                                <div className="inline-flex items-center rounded-md border border-slate-100 bg-slate-100/50 px-2 py-1.5 text-xs font-medium text-slate-400">
                                    +{message.snippets.length - 5} more
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
